<template>
  <v-data-table
    :headers="headers"
    :items="pouInstances"
    :search="search"
    sort-by="calories"
    class="elevation-1"
  >
    <template v-slot:top>
      <v-toolbar flat color="white">
        <v-toolbar-title>Instances</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
        ></v-text-field>
        <v-spacer></v-spacer>
        <v-dialog v-model="dialog" max-width="500px">
          <template v-slot:activator="{ on, attrs }">
            <v-btn class="mb-2" v-bind="attrs" v-on="on">Add Instance</v-btn>
          </template>
          <v-card>
            <validation-observer v-slot="{ handleSubmit }">
              <form @submit.prevent="handleSubmit(save)">
                <v-card-title class="headline grey lighten-2" primary-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>

                <v-card-text>
                  <v-container>
                    <v-row>
                      <v-col cols="6">
                        <validation-provider
                          :vid="uuid()"
                          name="Name"
                          :rules="`required|uniqueName:${existingInstanceNames}`"
                          v-slot="{ errors }"
                        >
                          <v-text-field label="Name" v-model="editedPouInstance.name"></v-text-field>
                          <span class="validation-message">
                            {{
                            errors[0]
                            }}
                          </span>
                        </validation-provider>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="6">
                        <validation-provider
                          :vid="uuid()"
                          name="Type"
                          rules="required"
                          v-slot="{ errors }"
                        >
                          <v-select
                            label="Type"
                            :items="pous"
                            item-text="name"
                            item-value="name"
                            v-model="editedPouInstance.typeName"
                          ></v-select>
                          <span class="validation-message">
                            {{
                            errors[0]
                            }}
                          </span>
                        </validation-provider>
                      </v-col>
                      <v-col cols="6">
                        <v-select
                          label="Task"
                          :items="tasks"
                          item-text="name"
                          item-value="tempId"
                          v-model="editedPouInstance.taskTempId"
                        ></v-select>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue darken-1" text @click="resetDialog">Cancel</v-btn>
                  <v-btn color="blue darken-1" text type="submit">Save</v-btn>
                </v-card-actions>
              </form>
            </validation-observer>
          </v-card>
        </v-dialog>
      </v-toolbar>
    </template>
    <template v-slot:item.actions="{ item }">
      <v-icon small class="mr-2" @click="editPouInstance(item)">mdi-pencil</v-icon>
      <v-icon small @click="deletePouInstance(item)">mdi-delete</v-icon>
    </template>
  </v-data-table>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { find, keys, flatMap, isNil, filter, map, pick } from "lodash";
import uuid from "src/mixins/uuid";

export default {
  name: "ResourceInstances",
  props: ["resourceTempId"],
  components: { ValidationProvider, ValidationObserver },
  mixins: [uuid],
  data: () => ({
    search: "",
    dialog: false,
    headers: [
      {
        text: "Name",
        align: "start",
        sortable: false,
        value: "name",
      },
      { text: "Type", value: "typeName" },
      { text: "Task", value: "taskName" },
      { text: "Actions", value: "actions", sortable: false },
    ],
    editedPouInstance: {},
  }),
  watch: {
    dialog(newValue) {
      if (!newValue) {
        this.resetDialog();
      }
    },
  },
  computed: {
    formTitle() {
      return this.editedPouInstance.tempId ? "Edit Instance" : "New Instance";
    },
    pous() {
      return this.$store.state.pous;
    },
    tasks() {
      return find(this.$store.state.configuration.resources, {
        tempId: this.resourceTempId,
      }).tasks;
    },
    pouInstances() {
      const resourcePouInstances = find(
        this.$store.state.configuration.resources,
        {
          tempId: this.resourceTempId,
        }
      ).pouInstances;

      const tasks = find(this.$store.state.configuration.resources, {
        tempId: this.resourceTempId,
      }).tasks;

      const tasksPouInstances = filter(
        flatMap(tasks, (task) =>
          map(task.pouInstances, (pouInstance) => ({
            ...pouInstance,
            taskTempId: task.tempId,
            taskName: task.name,
          }))
        ),
        (instance) => !isNil(instance)
      );

      return resourcePouInstances.concat(tasksPouInstances);
    },
    existingInstanceNames() {
      let instances = [];

      const resourcePouInstances = find(
        this.$store.state.configuration.resources,
        {
          tempId: this.resourceTempId,
        }
      ).pouInstances;

      const tasks = find(this.$store.state.configuration.resources, {
        tempId: this.resourceTempId,
      }).tasks;

      const tasksPouInstances = filter(
        flatMap(tasks, (task) => task.pouInstances),
        (instance) => !isNil(instance)
      );

      const pouInstances = resourcePouInstances.concat(tasksPouInstances);

      if (this.editedPouInstance.tempId) {
        instances = filter(
          pouInstances,
          (instance) => instance.tempId !== this.editedPouInstance.tempId
        );
      } else {
        instances = pouInstances;
      }

      return map(instances, "name").join(";");
    },
  },
  methods: {
    keys(obj) {
      return keys(obj);
    },
    editPouInstance(pouInstance) {
      this.editedPouInstance = Object.assign({}, pouInstance);
      this.dialog = true;
    },
    async deletePouInstance(pouInstance) {
      if (
        await this.$confirm("Are you sure you want to delete this instance?")
      ) {
        if (pouInstance.taskId) {
          this.$store.dispatch("deleteResourceTaskPouInstance", {
            resourceTempId: this.resourceTempId,
            taskTempId: pouInstance.taskTempId,
            pouInstanceTempId: pouInstance.tempId,
          });
        } else {
          this.$store.dispatch("deleteResourcePouInstance", {
            resourceTempId: this.resourceTempId,
            pouInstanceTempId: pouInstance.tempId,
          });
        }
      }
    },
    resetDialog() {
      this.dialog = false;
      this.editedPouInstance = {};
    },
    save() {
      if (!this.editedPouInstance.tempId) {
        // Add new pou instance
        if (this.editedPouInstance.taskTempId) {
          // Add to task
          this.$store.dispatch("addResourceTaskPouInstance", {
            resourceTempId: this.resourceTempId,
            taskTempId: this.editedPouInstance.taskTempId,
            pouInstance: {
              tempId: this.uuid(),
              ...pick(this.editedPouInstance, ["name", "typeName"]),
            },
          });
        } else {
          // Add to resource
          this.$store.dispatch("addResourcePouInstance", {
            resourceTempId: this.resourceTempId,
            pouInstance: {
              tempId: this.uuid(),
              ...pick(this.editedPouInstance, ["name", "typeName"]),
            },
          });
        }
      } else {
        // Update pou instance
        if (this.editedPouInstance.taskTempId) {
          this.$store.dispatch("updateResourceTaskPouInstance", {
            // Update to task
            resourceTempId: this.resourceTempId,
            taskTempId: this.editedPouInstance.taskTempId,
            pouInstance: pick(this.editedPouInstance, [
              "id",
              "tempId",
              "name",
              "typeName",
            ]),
          });
        } else {
          // Update to resource
          this.$store.dispatch("updateResourcePouInstance", {
            resourceTempId: this.resourceTempId,
            pouInstance: pick(this.editedPouInstance, [
              "id",
              "tempId",
              "name",
              "typeName",
            ]),
          });
        }
      }

      this.resetDialog();
    },
  },
};
</script>
